//libs
import React, { useCallback, useMemo, useRef, useState } from 'react';
import c from 'classnames';

import { useTranslation } from '@lyearn/i18n';
import { FileDragDrop, Link2Outline } from '@lyearn/icons';
//components
import {
  BaseButton,
  BasePopoverContent,
  Box,
  InputBase,
  LinkButton,
  PopoverOrigin,
  Stack,
  Typography,
  Upload,
  useSnackbar,
} from '@lyearn/ui';

//hooks
import { useHandleInputChange } from '../../hooks/useHandleInputChange';
//styles
import {
  buttonIcon,
  iconStyles,
  inputStyles,
  popoverContainer,
  uploadButton,
  uploadInput,
} from './styles';
//types
import { EmbedPopoverProps } from './types';
//utils
import { getIframeSrc } from './utils';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};

const getClasses = (isUploadButton: boolean) => ({
  paper: c('rounded-8 mt-8 border border-divider', isUploadButton ? 'shadow-01' : 'shadow-02'),
});

const renderInputPlaceholder = (placeholderText: string) => (
  <Stack alignItems="center" className="w-full opacity-100" direction="col">
    <FileDragDrop className={c(iconStyles, '-my-24')} width={170} />
    <Typography
      className="hover:text-interactive-primary"
      color="text-text-placeholder"
      variant="heading-xxs">
      {placeholderText}
    </Typography>
  </Stack>
);

const EmbedPopover: React.FC<EmbedPopoverProps> = (props) => {
  const {
    id,
    setEmbedUrl,
    validFileTypes,
    placeholderString,
    inputPlaceholderString,
    isUploadButton = true,
    inputButtonString,
    PlaceholderIcon,
  } = props;
  const { handleInputChange } = useHandleInputChange(id!);
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toggle = useCallback(() => setOpen((isOpen) => !isOpen), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const [embedUrlInput, setEmbedUrlInput] = useState<string>('');

  const inputPlaceholder = useMemo(() => renderInputPlaceholder(t('Common:UploadFromDevice')), [t]);

  const { showErrorSnackbar } = useSnackbar();

  const onEmbedClick = useCallback(() => {
    if (embedUrlInput) {
      const { parsedUrl, url } = getIframeSrc(embedUrlInput);
      parsedUrl || url
        ? setEmbedUrl(parsedUrl, url)
        : showErrorSnackbar({
            message: 'Invalid url',
            position: { horizontal: 'right', vertical: 'bottom' },
          });
    }
  }, [embedUrlInput, setEmbedUrl, showErrorSnackbar]);

  const onFileSelectErrorHandler = useCallback(() => {
    showErrorSnackbar({
      message: t('Common:FileTypeNotAllowedToBeUploaded', { accept: validFileTypes }),
    });
  }, [showErrorSnackbar, validFileTypes, t]);

  return (
    <Box className="my-32">
      <BaseButton
        ref={buttonRef}
        fullWidth
        className={c('justify-start rounded-8', uploadButton)}
        // @ts-ignore
        component="span"
        onClick={toggle}>
        <PlaceholderIcon className={buttonIcon} height={40} width={32} />
        <Typography className="text-text-primary ps-16" variant="body-short-02">
          {placeholderString}
        </Typography>
      </BaseButton>
      <BasePopoverContent
        disableRestoreFocus
        anchorEl={buttonRef.current}
        anchorOrigin={ANCHOR_ORIGIN}
        classes={getClasses(isUploadButton)}
        open={open}
        transformOrigin={TRANSFORM_ORIGIN}
        onClose={handleClose}>
        <Stack
          className={c(popoverContainer(isUploadButton), isUploadButton ? 'p-16' : '')}
          direction="col">
          {isUploadButton && (
            <Upload
              accept={validFileTypes!}
              className={uploadInput}
              placeholder={inputPlaceholder}
              onChange={handleInputChange}
              onFileSelectError={onFileSelectErrorHandler}
            />
          )}
          <Stack
            alignItems="center"
            className={c(
              'h-40 w-full flex-shrink-0 rounded-8 p-12',
              isUploadButton ? 'mt-16 border border-border' : '',
            )}>
            <Link2Outline className="text-icon-placeholder" height={20} width={20} />
            <InputBase
              autoFocus
              className={c('body-short-02 truncate ps-12', inputStyles)}
              placeholder={inputPlaceholderString}
              value={embedUrlInput}
              onChange={(event) => setEmbedUrlInput(event.target.value.trim())}
            />
            {embedUrlInput && (
              <LinkButton className="justify-self-end py-2 px-4 ms-16" onClick={onEmbedClick}>
                <Typography variant="heading-xxs">{inputButtonString}</Typography>
              </LinkButton>
            )}
          </Stack>
        </Stack>
      </BasePopoverContent>
    </Box>
  );
};

export default EmbedPopover;
